/**
 * LmsCatalogExplorer — admin-only debug drill-down through the raw LMS
 * cenikový katalog.
 *
 * Lives under `/management?tab=lms-explorer`. The production Ford picker
 * (`FordLmsPicker`) and the new-vehicle / nabídka wizards consume the
 * `/v1/vehicles/lms/*` cascade, which **dedupes model years by `KodModelu`
 * and caps pricelists at the 10 most recent** — so a model year or older
 * ceník the factory still serves can silently disappear from the picker.
 *
 * This explorer hits the parallel `/v1/management/lms/catalog/*` endpoints
 * that call the same repository with `{ raw: true }`, so every level shows
 * *everything LMS returns*. The cascade mirrors the picker:
 *
 *   Model → Modelový rok → Ceník → Entita → Výbava
 *
 * Each level lists the key fields inline and exposes the complete upstream
 * payload via a "Surová odpověď (JSON)" toggle — the point of the page is
 * debugging, so nothing is hidden.
 */

import { useState } from 'react';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Marker } from '@/components/Marker';
import { Skeleton } from '@/components/Skeleton';
import { Toolband } from '@/components/Toolband';
import { ApiError } from '@/lib/api';
import { formatInt } from '@/lib/format';
import {
  useLmsCatalogModelsQuery,
  useLmsCatalogModelYearsQuery,
  useLmsCatalogPricelistsQuery,
  useLmsCatalogEntitiesQuery,
  useLmsCatalogEquipmentQuery,
  type LmsCatalogRecord,
} from '@/modules/management/api';

interface LmsCatalogExplorerProps {
  isAdmin: boolean;
}

interface Column {
  key: string;
  label: string;
  /** Right-aligned monospace column (IDs, years, prices). No thousands grouping. */
  numeric?: boolean;
  /** Apply thousands-grouped integer formatting — prices only, never IDs/years. */
  money?: boolean;
}

const MODEL_COLS: Column[] = [
  { key: 'ID', label: 'ID', numeric: true },
  { key: 'Nazev', label: 'Název' },
];
const YEAR_COLS: Column[] = [
  { key: 'KodModelu', label: 'Kód modelu' },
  { key: 'NazevModelu', label: 'Název modelu' },
  { key: 'Rok', label: 'Rok', numeric: true },
];
const PRICELIST_COLS: Column[] = [
  { key: 'ID', label: 'ID', numeric: true },
  { key: 'NazevModelu', label: 'Název modelu' },
  { key: 'PlatnostOd', label: 'Platnost od' },
  { key: 'DatumVydani', label: 'Datum vydání' },
  { key: 'CenaSDPH', label: 'Cena s DPH' },
];
const ENTITY_COLS: Column[] = [
  { key: 'ID', label: 'ID', numeric: true },
  { key: 'Oznaceni', label: 'Označení' },
  { key: 'Motor', label: 'Motor' },
  { key: 'Prevodovka', label: 'Převodovka' },
  { key: 'StupenVybavy', label: 'Stupeň výbavy' },
  { key: 'Cena', label: 'Cena', numeric: true, money: true },
];

function str(v: unknown): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Ano' : 'Ne';
  return String(v);
}

export function LmsCatalogExplorer({ isAdmin }: LmsCatalogExplorerProps) {
  // Selected record at each cascade level. Storing the whole record (not just
  // the id) lets the breadcrumb echo names and keeps the next level's query
  // params (model.ID + modelYear.KodModelu) co-located.
  const [model, setModel] = useState<LmsCatalogRecord | null>(null);
  const [year, setYear] = useState<LmsCatalogRecord | null>(null);
  const [pricelist, setPricelist] = useState<LmsCatalogRecord | null>(null);
  const [entity, setEntity] = useState<LmsCatalogRecord | null>(null);
  // Models are an expensive HMAC-signed call — don't auto-fetch on mount.
  const [started, setStarted] = useState(false);

  const modelId = model ? String(model.ID ?? '') : null;
  const modelCode = year ? String(year.KodModelu ?? '') : null;
  const pricelistId = pricelist ? String(pricelist.ID ?? '') : null;
  const entityId = entity ? String(entity.ID ?? '') : null;

  const models = useLmsCatalogModelsQuery(started);
  const years = useLmsCatalogModelYearsQuery(modelId);
  const pricelists = useLmsCatalogPricelistsQuery(modelId, modelCode);
  const entities = useLmsCatalogEntitiesQuery(pricelistId);
  const equipment = useLmsCatalogEquipmentQuery(entityId);

  if (!isAdmin) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">
          <EmptyState
            eyebrow="403"
            title="LMS průzkum je dostupný jen administrátorům."
            description="Pro přístup kontaktuj správce firmy."
          />
        </div>
      </div>
    );
  }

  // Identity is by object reference, NOT by a derived id: in raw mode two
  // model-year rows can share the same KodModelu (differing only by Rok), so
  // an id-based guard would treat both as "the same selection" — selecting one
  // would highlight both and block switching between them.
  const onModel = (rec: LmsCatalogRecord) => {
    if (rec === model) return;
    setModel(rec);
    setYear(null);
    setPricelist(null);
    setEntity(null);
  };
  const onYear = (rec: LmsCatalogRecord) => {
    if (rec === year) return;
    setYear(rec);
    setPricelist(null);
    setEntity(null);
  };
  const onPricelist = (rec: LmsCatalogRecord) => {
    if (rec === pricelist) return;
    setPricelist(rec);
    setEntity(null);
  };
  const onEntity = (rec: LmsCatalogRecord) => {
    setEntity(rec);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Toolband>
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          LMS průzkum katalogu
        </span>
        <Toolband.Spacer />
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Surová data z LMS — bez filtrování, jaké používá výběr vozu.
        </span>
      </Toolband>

      <div
        data-testid="management-lms-explorer"
        className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-8 pt-5 max-sm:px-4"
      >
        {/* Breadcrumb of the active drill path */}
        <Trail
          model={model ? str(model.Nazev) : null}
          year={year ? `${str(year.KodModelu)} · ${str(year.Rok)}` : null}
          pricelist={pricelist ? str(pricelist.NazevModelu) : null}
          entity={entity ? str(entity.Oznaceni) : null}
        />

        {/* 01 — Models */}
        <Level
          n="01"
          title="Model"
          description="Kompletní seznam modelů z LMS, seřazený dle názvu."
          query={models}
          rows={models.data?.models}
          columns={MODEL_COLS}
          getId={(r) => str(r.ID)}
          selectedRow={model}
          onSelect={onModel}
          action={
            !started ? (
              <Button
                variant="primary"
                size="sm"
                icon={<Icon name="download" size={12} />}
                onClick={() => setStarted(true)}
              >
                Načíst modely
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                icon={<Icon name="download" size={12} />}
                onClick={() => models.refetch()}
                disabled={models.isFetching}
              >
                {models.isFetching ? 'Načítám…' : 'Obnovit'}
              </Button>
            )
          }
          idle={!started ? 'Klikněte „Načíst modely“ pro stažení katalogu z LMS.' : undefined}
        />

        {/* 02 — Model years */}
        {model && (
          <Level
            n="02"
            title="Modelový rok"
            description="Všechny modelové roky — včetně duplicit kódu modelu (KodModelu), které výběr vozu slučuje. Pozor: ceníky LMS filtruje podle kódu modelu, ne podle roku — řádky se stejným KodModelu proto vrací v kroku 03 stejný seznam."
            query={years}
            rows={years.data?.modelYears}
            columns={YEAR_COLS}
            getId={(r) => str(r.KodModelu)}
            selectedRow={year}
            onSelect={onYear}
          />
        )}

        {/* 03 — Pricelists */}
        {year && (
          <Level
            n="03"
            title="Ceník"
            description={`Všechny ceníky pro kód modelu „${str(year.KodModelu)}" (id_modelu ${str(model?.ID)}, mid ${str(year.KodModelu)}) — bez limitu 10 nejnovějších, který používá výběr vozu. LMS filtruje pouze podle kódu modelu, ne podle roku, takže seznam je stejný pro všechny modelové roky se stejným KodModelu.`}
            query={pricelists}
            rows={pricelists.data?.pricelists}
            columns={PRICELIST_COLS}
            getId={(r) => str(r.ID)}
            selectedRow={pricelist}
            onSelect={onPricelist}
          />
        )}

        {/* 04 — Entities */}
        {pricelist && (
          <Level
            n="04"
            title="Entita / provedení"
            description="Provedení (entity) v rámci vybraného ceníku."
            query={entities}
            rows={entities.data?.entities}
            columns={ENTITY_COLS}
            getId={(r) => str(r.ID)}
            selectedRow={entity}
            onSelect={onEntity}
          />
        )}

        {/* 05 — Equipment (leaf) */}
        {entity && <EquipmentLevel query={equipment} />}
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────── */

interface TrailProps {
  model: string | null;
  year: string | null;
  pricelist: string | null;
  entity: string | null;
}

function Trail({ model, year, pricelist, entity }: TrailProps) {
  const parts = [
    { label: 'Model', value: model },
    { label: 'Rok', value: year },
    { label: 'Ceník', value: pricelist },
    { label: 'Entita', value: entity },
  ].filter((p) => p.value);

  if (parts.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border border-line bg-bg px-3 py-2 text-[12px]">
      {parts.map((p, i) => (
        <span key={p.label} className="flex items-center gap-2">
          {i > 0 && <span className="text-ink-3">›</span>}
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
            {p.label}
          </span>
          <span className="text-ink">{p.value}</span>
        </span>
      ))}
    </div>
  );
}

interface LevelProps {
  n: string;
  title: string;
  description: string;
  query: { isFetching: boolean; isError: boolean; error: unknown; data: unknown };
  rows: LmsCatalogRecord[] | undefined;
  columns: Column[];
  getId: (r: LmsCatalogRecord) => string;
  /** Selected row, compared by object reference (see onSelect handlers). */
  selectedRow: LmsCatalogRecord | null;
  onSelect: (r: LmsCatalogRecord) => void;
  action?: React.ReactNode;
  /** Shown instead of the table before the first fetch is triggered. */
  idle?: string;
}

function Level({
  n,
  title,
  description,
  query,
  rows,
  columns,
  getId,
  selectedRow,
  onSelect,
  action,
  idle,
}: LevelProps) {
  let body: React.ReactNode;
  if (idle) {
    body = <CardEmpty>{idle}</CardEmpty>;
  } else if (query.isFetching && !rows) {
    body = <Skeleton className="h-24 w-full" />;
  } else if (query.isError) {
    body = <CardEmpty>Chyba: {errMsg(query.error)}</CardEmpty>;
  } else if (!rows) {
    body = <Skeleton className="h-24 w-full" />;
  } else if (rows.length === 0) {
    body = <CardEmpty>LMS pro tuto úroveň nevrátilo žádné záznamy.</CardEmpty>;
  } else {
    body = (
      <div className="flex flex-col gap-3">
        <RecordTable
          rows={rows}
          columns={columns}
          getId={getId}
          selectedRow={selectedRow}
          onSelect={onSelect}
        />
        <RawJson data={rows} />
      </div>
    );
  }

  return (
    <section className="border border-line bg-paper">
      <header className="flex items-start gap-4 border-b border-line px-5 py-4 max-sm:flex-wrap max-sm:px-4">
        <span className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-accent-text">
          {n}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-3">
            <h3 className="font-display text-[20px] leading-none tracking-[-0.02em] text-ink">
              {title}
            </h3>
            {rows && (
              <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                {formatInt(rows.length)} {rows.length === 1 ? 'záznam' : 'záznamů'}
              </span>
            )}
          </div>
          <p className="mt-1.5 text-[12px] leading-[1.5] text-ink-3">{description}</p>
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </header>
      <div className="p-5">{body}</div>
    </section>
  );
}

interface RecordTableProps {
  rows: LmsCatalogRecord[];
  columns: Column[];
  getId: (r: LmsCatalogRecord) => string;
  selectedRow: LmsCatalogRecord | null;
  onSelect: (r: LmsCatalogRecord) => void;
}

function RecordTable({ rows, columns, getId, selectedRow, onSelect }: RecordTableProps) {
  // Fixed first column for the radio dot, then one fraction per data column.
  const template = `28px ${columns
    .map((c) => (c.numeric ? '120px' : 'minmax(0,1fr)'))
    .join(' ')}`;

  return (
    <div className="overflow-hidden border border-line bg-paper max-sm:overflow-x-auto">
      <div
        className="grid items-center gap-2 border-b border-line bg-bg px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3 max-sm:min-w-[640px]"
        style={{ gridTemplateColumns: template }}
      >
        <span />
        {columns.map((c) => (
          <span key={c.key} className={c.numeric ? 'text-right' : undefined}>
            {c.label}
          </span>
        ))}
      </div>
      <ul className="divide-y divide-line max-sm:min-w-[640px]">
        {rows.map((r, idx) => {
          const id = getId(r);
          const selected = r === selectedRow;
          return (
            <li key={`${id}-${idx}`}>
              <button
                type="button"
                onClick={() => onSelect(r)}
                data-selected={selected ? 'true' : 'false'}
                aria-pressed={selected}
                className={
                  'grid w-full items-center gap-2 px-3 py-2 text-left text-[12.5px] hover:bg-bg ' +
                  (selected ? 'bg-[var(--accent-soft)]' : '')
                }
                style={{ gridTemplateColumns: template }}
              >
                <span
                  aria-hidden
                  className={
                    'inline-block h-2.5 w-2.5 rounded-full border ' +
                    (selected ? 'border-accent bg-accent' : 'border-line')
                  }
                />
                {columns.map((c) => {
                  const raw = r[c.key];
                  const display =
                    c.money && (typeof raw === 'number' || (typeof raw === 'string' && raw !== ''))
                      ? formatInt(raw as number | string)
                      : str(raw);
                  return (
                    <span
                      key={c.key}
                      className={
                        c.numeric
                          ? 'text-right font-mono tabular-nums text-ink-2'
                          : 'truncate text-ink'
                      }
                    >
                      {display}
                    </span>
                  );
                })}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function EquipmentLevel({
  query,
}: {
  query: {
    isFetching: boolean;
    isError: boolean;
    error: unknown;
    data: { equipment: LmsCatalogRecord } | undefined;
  };
}) {
  let body: React.ReactNode;
  if (query.isFetching && !query.data) {
    body = <Skeleton className="h-24 w-full" />;
  } else if (query.isError) {
    body = <CardEmpty>Chyba: {errMsg(query.error)}</CardEmpty>;
  } else if (!query.data) {
    body = <Skeleton className="h-24 w-full" />;
  } else {
    const eq = query.data.equipment ?? {};
    const groups: { key: string; label: string }[] = [
      { key: 'barvy', label: 'Barvy' },
      { key: 'calouneni', label: 'Čalounění' },
      { key: 'standardniVybava', label: 'Standardní výbava' },
      { key: 'priplatkovaVybava', label: 'Příplatková výbava' },
    ];
    body = (
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          {groups.map((g) => {
            const arr = eq[g.key];
            const count = Array.isArray(arr) ? arr.length : 0;
            return (
              <span
                key={g.key}
                className="flex items-center gap-2 border border-line bg-bg px-3 py-1.5 text-[12px]"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                  {g.label}
                </span>
                <Marker tone={count > 0 ? 'accent' : 'neutral'}>{formatInt(count)}</Marker>
              </span>
            );
          })}
        </div>
        <RawJson data={eq} defaultOpen />
      </div>
    );
  }

  return (
    <section className="border border-line bg-paper">
      <header className="flex items-start gap-4 border-b border-line px-5 py-4 max-sm:flex-wrap max-sm:px-4">
        <span className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-accent-text">
          05
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-[20px] leading-none tracking-[-0.02em] text-ink">
            Výbava
          </h3>
          <p className="mt-1.5 text-[12px] leading-[1.5] text-ink-3">
            Barvy, čalounění a standardní/příplatková výbava pro vybranou entitu — celá odpověď LMS.
          </p>
        </div>
      </header>
      <div className="p-5">{body}</div>
    </section>
  );
}

function RawJson({ data, defaultOpen }: { data: unknown; defaultOpen?: boolean }) {
  return (
    <details className="border border-line bg-bg" open={defaultOpen}>
      <summary className="cursor-pointer select-none px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3 hover:text-ink-2">
        Surová odpověď (JSON)
      </summary>
      <pre className="max-h-[400px] overflow-auto border-t border-line px-3 py-2 text-[11px] leading-[1.5] text-ink-2">
        {JSON.stringify(data, null, 2)}
      </pre>
    </details>
  );
}

function CardEmpty({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-1 py-3 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
      {children}
    </p>
  );
}

function errMsg(err: unknown): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const e = (err.body as { error?: string }).error;
    if (typeof e === 'string') return e;
  }
  if (err instanceof Error) return err.message;
  return 'LMS volání selhalo.';
}
